/*
 * rag-fitment.js  (v2 — fetched + cached index)
 * Shared engine for Rhino "Shop by Vehicle".
 *  - loads the fitment index from /pages/fitment-data (paginated), caches in
 *    localStorage keyed by a version stamp, reads cache thereafter
 *  - cascading Year -> Make -> Model selector, collapses to a "chip" once set
 *  - persistent garage (localStorage) + header icon label sync
 *  - client-side matching (year-in-range) for results, collection filter, PDP
 *  - public API: window.RAGFitment
 */
(function () {
  'use strict';
  if (window.__ragFitmentLoaded) return;
  window.__ragFitmentLoaded = true;

  var GARAGE_KEY = 'rag_garage_v1';
  var INDEX_KEY = 'rag_index_v1';
  var VERSION = window.RAG_FITMENT_VERSION || '0';
  var ENDPOINT = window.RAG_FITMENT_ENDPOINT || '/pages/fitment-data';
  var RESULTS_URL = window.RAG_RESULTS_URL || '/pages/shop-by-vehicle';

  var DATA = [];
  var ready = false;
  var readyCallbacks = [];

  // ---- Index load (fetch + cache) -------------------------------------
  function parseToken(t) {
    var p = String(t).split('|');
    if (p.length !== 3) return null;
    var s = p[2].split('-');
    var lo = parseInt(s[0], 10), hi = parseInt(s[1], 10);
    if (isNaN(lo)) return null;
    if (isNaN(hi)) hi = lo;
    return { make: p[0].trim(), model: p[1].trim(), lo: lo, hi: hi };
  }
  function hydrate(list) {
    DATA = (list || []).filter(function (p) { return p && p.toks && p.toks.length; });
    DATA.forEach(function (p) { p._fit = p.toks.map(parseToken).filter(Boolean); });
  }
  function fromCache() {
    try {
      var raw = localStorage.getItem(INDEX_KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (obj.v !== VERSION) return null;
      if (!obj.d || !obj.d.length) return null; // empty cache is not valid
      return obj.d;
    } catch (e) { return null; }
  }
  function toCache(list) {
    if (!list || !list.length) return; // never cache an empty result
    try { localStorage.setItem(INDEX_KEY, JSON.stringify({ v: VERSION, d: list })); }
    catch (e) {}
  }
  function fetchAll(cb) {
    var all = [], page = 1;
    function next() {
      fetch(ENDPOINT + '?page=' + page)
        .then(function (r) { return r.json(); })
        .then(function (data) {
          all = all.concat(data.products || []);
          if (page < (data.total_pages || 1)) { page++; next(); }
          else { toCache(all); cb(all); }
        })
        .catch(function (err) {
          if (window.RAG_DEBUG) console.error('[RAG] fetch failed', err);
          cb(all);
        });
    }
    next();
  }
  function loadIndex(cb) {
    var cached = fromCache();
    if (cached && cached.length) {
      if (window.RAG_DEBUG) console.log('[RAG] from cache:', cached.length);
      hydrate(cached); cb(); return;
    }
    if (window.RAG_DEBUG) console.log('[RAG] fetching index from', ENDPOINT);
    fetchAll(function (list) {
      if (window.RAG_DEBUG) console.log('[RAG] fetched:', list.length);
      hydrate(list); cb();
    });
  }
  function onReady(fn) {
    if (ready) fn(); else readyCallbacks.push(fn);
  }

  // ---- Cascade queries -------------------------------------------------
  function allYears() {
    var lo = Infinity, hi = -Infinity;
    DATA.forEach(function (p) { p._fit.forEach(function (f) {
      if (f.lo < lo) lo = f.lo; if (f.hi > hi) hi = f.hi;
    }); });
    var out = []; if (lo === Infinity) return out;
    for (var y = hi; y >= lo; y--) out.push(y);
    return out;
  }
  function makesForYear(y) {
    var s = {};
    DATA.forEach(function (p) { p._fit.forEach(function (f) {
      if (y >= f.lo && y <= f.hi) s[f.make] = 1; }); });
    return Object.keys(s).sort();
  }
  function modelsFor(y, mk) {
    var s = {};
    DATA.forEach(function (p) { p._fit.forEach(function (f) {
      if (f.make === mk && y >= f.lo && y <= f.hi) s[f.model] = 1; }); });
    return Object.keys(s).sort(function (a, b) {
      return a.localeCompare(b, undefined, { numeric: true }); });
  }
  function matches(y, mk, md) {
    return DATA.filter(function (p) { return p._fit.some(function (f) {
      return f.make === mk && f.model === md && y >= f.lo && y <= f.hi; }); });
  }
  function productFits(handle, g) {
    if (!g || !g.year) return null;
    var p = DATA.filter(function (x) { return x.h === handle; })[0];
    if (!p) return null; // not a fitment product
    return p._fit.some(function (f) {
      return f.make === g.make && f.model === g.model && g.year >= f.lo && g.year <= f.hi;
    });
  }

  // ---- Garage ----------------------------------------------------------
  function getGarage() {
    try { return JSON.parse(localStorage.getItem(GARAGE_KEY)); } catch (e) { return null; }
  }
  function setGarage(g) {
    try { localStorage.setItem(GARAGE_KEY, JSON.stringify(g)); } catch (e) {}
    syncChrome();
    document.dispatchEvent(new CustomEvent('rag:garage-change'));
  }
  function clearGarage() {
    try { localStorage.removeItem(GARAGE_KEY); } catch (e) {}
    syncChrome();
    document.dispatchEvent(new CustomEvent('rag:garage-change'));
  }
  function label(g) { return g ? [g.year, g.make, g.model].filter(Boolean).join(' ') : ''; }
  function shortLabel(g) { return g ? [g.year, g.model].filter(Boolean).join(' ') : ''; }

  // Update header icon label + any chips across the page
  function syncChrome() {
    var g = getGarage();
    document.querySelectorAll('[data-rag-garage-label]').forEach(function (el) {
      el.textContent = g ? shortLabel(g) : (el.getAttribute('data-empty') || 'Shop by Vehicle');
    });
    document.querySelectorAll('[data-rag-garage-icon]').forEach(function (el) {
      el.classList.toggle('rag-has-vehicle', !!g);
    });
    document.querySelectorAll('[data-rag-selector]').forEach(renderSelectorState);
  }

  // ---- Dropdown helpers ------------------------------------------------
  function fill(sel, items, placeholder) {
    sel.innerHTML = '';
    var o = document.createElement('option');
    o.value = ''; o.textContent = placeholder; sel.appendChild(o);
    items.forEach(function (v) {
      var x = document.createElement('option'); x.value = v; x.textContent = v; sel.appendChild(x);
    });
  }

  // ---- Selector (dropdowns <-> chip) -----------------------------------
  function renderSelectorState(root) {
    var g = getGarage();
    var fields = root.querySelector('[data-rag-fields]');
    var chip = root.querySelector('[data-rag-chip]');
    if (!fields || !chip) return;
    if (g && g.year) {
      fields.hidden = true; chip.hidden = false;
      var lbl = chip.querySelector('[data-rag-chip-label]');
      if (lbl) lbl.textContent = label(g);
    } else {
      fields.hidden = false; chip.hidden = true;
    }
  }

  function initSelector(root) {
    var yearEl = root.querySelector('[data-rag-year]');
    var makeEl = root.querySelector('[data-rag-make]');
    var modelEl = root.querySelector('[data-rag-model]');
    var goEl = root.querySelector('[data-rag-go]');
    var editEl = root.querySelector('[data-rag-edit]');
    var clearEl = root.querySelector('[data-rag-clear]');
    var mode = root.getAttribute('data-rag-mode') || 'redirect';
    var resultsUrl = root.getAttribute('data-rag-results-url') || RESULTS_URL;

    // Prevent the native select picker (esp. iOS) from bubbling click/focus
    // events up to Mojave's menu-drawer, which would otherwise close the menu
    // when a Model is chosen, before the user can hit "Find my fit".
    [yearEl, makeEl, modelEl].forEach(function (sel) {
      ['click', 'mousedown', 'touchstart', 'focusout', 'change'].forEach(function (evt) {
        sel.addEventListener(evt, function (e) { e.stopPropagation(); });
      });
    });

    fill(yearEl, allYears(), 'Year');
    function resetMake() { fill(makeEl, [], 'Make'); makeEl.disabled = true; }
    function resetModel() { fill(modelEl, [], 'Model'); modelEl.disabled = true; }
    function syncGo() { goEl.disabled = !(yearEl.value && makeEl.value && modelEl.value); }

    yearEl.addEventListener('change', function () {
      resetModel();
      if (yearEl.value) { fill(makeEl, makesForYear(+yearEl.value), 'Make'); makeEl.disabled = false; }
      else resetMake();
      syncGo();
    });
    makeEl.addEventListener('change', function () {
      if (yearEl.value && makeEl.value) { fill(modelEl, modelsFor(+yearEl.value, makeEl.value), 'Model'); modelEl.disabled = false; }
      else resetModel();
      syncGo();
    });
    modelEl.addEventListener('change', syncGo);

    goEl.addEventListener('click', function () {
      setGarage({ year: +yearEl.value, make: makeEl.value, model: modelEl.value });
      // If a collection vehicle-filter is on this page, filter in place instead
      // of redirecting away — the rag:garage-change event does the filtering.
      var onCollectionFilter = document.querySelector('[data-rag-collection-filter]');
      if (mode === 'redirect' && !onCollectionFilter) {
        window.location.href = resultsUrl;
        return;
      }
      document.querySelectorAll('[data-rag-garage-details][open]').forEach(function (d) { d.removeAttribute('open'); });
      renderSelectorState(root);
      if (mode !== 'redirect') renderResults(getGarage());
    });

    if (editEl) editEl.addEventListener('click', function () {
      // reveal fields without clearing, pre-filled
      var g = getGarage(); root.querySelector('[data-rag-fields]').hidden = false;
      root.querySelector('[data-rag-chip]').hidden = true;
      if (g) prefill(g);
    });
    if (clearEl) clearEl.addEventListener('click', function () {
      clearGarage(); yearEl.value = ''; resetMake(); resetModel(); syncGo();
      if (mode !== 'redirect') renderResults(null);
    });

    function prefill(g) {
      yearEl.value = String(g.year);
      fill(makeEl, makesForYear(g.year), 'Make'); makeEl.disabled = false;
      if (g.make) { makeEl.value = g.make; fill(modelEl, modelsFor(g.year, g.make), 'Model'); modelEl.disabled = false; if (g.model) modelEl.value = g.model; }
      syncGo();
    }
    var saved = getGarage();
    if (saved && saved.year) prefill(saved);
    renderSelectorState(root);
  }

  // ---- Results rendering ----------------------------------------------
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function card(p) {
    var a = document.createElement('a');
    a.className = 'rag-vres__card'; a.href = p.u;
    a.innerHTML = (p.img
      ? '<div class="rag-vres__media"><img src="' + p.img + '" alt="" loading="lazy"></div>'
      : '<div class="rag-vres__media rag-vres__media--empty"></div>') +
      '<div class="rag-vres__info"><span class="rag-vres__title">' + esc(p.t) +
      '</span><span class="rag-vres__price">' + esc(p.price || '') + '</span></div>';
    return a;
  }
  function renderResults(g) {
    var host = document.querySelector('[data-rag-results]');
    if (!host) return;
    var grid = host.querySelector('[data-rag-results-grid]');
    var head = host.querySelector('[data-rag-results-head]');
    var empty = host.querySelector('[data-rag-results-empty]');
    if (!grid) return;
    grid.innerHTML = '';
    if (!g || !g.year || !g.make || !g.model) {
      if (head) head.textContent = 'Choose your vehicle to see what fits.';
      if (empty) empty.hidden = true; return;
    }
    var found = matches(g.year, g.make, g.model);
    if (head) head.textContent = found.length
      ? found.length + (found.length === 1 ? ' result' : ' results') + ' for your ' + label(g)
      : 'No vehicle-specific gear for your ' + label(g) + ' yet';
    if (empty) empty.hidden = found.length > 0;
    found.forEach(function (p) { grid.appendChild(card(p)); });
  }

  // ---- Boot ------------------------------------------------------------
  function boot() {
    // show loading state on selectors while the index loads
    document.querySelectorAll('[data-rag-selector]').forEach(function (r) {
      r.setAttribute('data-rag-loading', 'true');
    });
    loadIndex(function () {
      ready = true;
      document.querySelectorAll('[data-rag-selector]').forEach(function (r) {
        r.removeAttribute('data-rag-loading'); initSelector(r);
      });
      if (document.querySelector('[data-rag-results]')) renderResults(getGarage());
      syncChrome();
      readyCallbacks.forEach(function (fn) { try { fn(); } catch (e) {} });
      readyCallbacks = [];
      document.dispatchEvent(new CustomEvent('rag:ready'));
    });
    syncChrome(); // labels reflect garage immediately, even before index loads
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Open / close the header garage dropdown (triggers: nav item, chip, etc.)
  document.addEventListener('click', function (e) {
    // open trigger (e.g. the "Shop by Vehicle" nav item) — toggles the drawer
    if (e.target.closest('[data-rag-open]')) {
      var dd = document.querySelector('[data-rag-garage-details]');
      if (dd) {
        if (dd.hasAttribute('open')) dd.removeAttribute('open');
        else dd.setAttribute('open', '');
      }
      e.preventDefault();
      return;
    }
    // close button
    if (e.target.closest('[data-rag-close]')) {
      var dc = e.target.closest('[data-rag-garage-details]');
      if (dc) dc.removeAttribute('open');
      return;
    }
    // outside click (desktop) OR backdrop tap (mobile ::after resolves to the details element)
    document.querySelectorAll('[data-rag-garage-details][open]').forEach(function (d) {
      if (!d.contains(e.target) || e.target === d) d.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      document.querySelectorAll('[data-rag-garage-details][open]').forEach(function (d) {
        d.removeAttribute('open');
      });
    }
  });

  // When the mobile inline "Shop by Vehicle" expands, bring the Find my fit
  // button into view so the user doesn't have to scroll the menu to find it.
  document.addEventListener('toggle', function (e) {
    var d = e.target;
    if (d && d.classList && d.classList.contains('menu-drawer-vehicle') && d.open) {
      var target = d.querySelector('[data-rag-go]') || d.querySelector('[data-rag-chip]:not([hidden])') || d;
      if (target && target.scrollIntoView) {
        setTimeout(function () { target.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }, 60);
      }
    }
  }, true);

  // ---- Public API ------------------------------------------------------
  function reload() {
    try { localStorage.removeItem(INDEX_KEY); } catch (e) {}
    DATA = []; ready = false;
    loadIndex(function () {
      ready = true;
      document.querySelectorAll('[data-rag-selector]').forEach(initSelector);
      if (document.querySelector('[data-rag-results]')) renderResults(getGarage());
      syncChrome();
      if (window.RAG_DEBUG) console.log('[RAG] reload complete:', DATA.length);
    });
  }

  window.RAGFitment = {
    onReady: onReady,
    reload: reload,
    getGarage: getGarage,
    setGarage: setGarage,
    clearGarage: clearGarage,
    label: label,
    matches: matches,
    productFits: productFits,
    get data() { return DATA; }
  };
})();
